<template>
  <div class="border-solid border-0 border-t border-soft h-80 p-5 text-sm cursor-auto flex flex-col">

    <validated-input :name="index.toString()"
                     :value="quickReply.title"
                     :validated-value="validatedValue"
                     label="Title"
                     placeholder="Quick reply label"
                     :error-message="showError"
                     :is-autofocus="focus === SECTION_FOCUSES.QUICK_REPLY + index"
                     :custom-length-limit="titleCountLimit"
                     @keyup="updateQuickReplyTitle($event.target.value)"
                     @blur="validateTitle"
    />

    <div>
      <base-label :id="`qrOutcome${index}`" label="Automation Result"/>
      <base-select :value="requestOutcome"
                   :options="requestOutcomeOptions"
                   :use-option-slot="true"
                   :use-label-slot="true"
                   class="w-full border-gray h-10 mt-1"
                   @input="updateRequestOutcome"
      >
        <template #option="{ option }">
          <span class="flex items-center">
            <component v-if="option.raw.icon" :is="option.raw.icon" class="h-3 w-3"/>
            <span v-html="option.text" class="capitalize" :class="option.raw.icon ? 'ml-2' : 'ml-5'"/>
          </span>
        </template>

        <template #label="{ option }">
          <span class="flex items-center">
            <component v-if="option.raw.icon" :is="option.raw.icon" class="h-3 w-3"/>
            <span v-html="option.text" class="capitalize" :class="{'ml-2': option.raw.icon}"/>
          </span>
        </template>
      </base-select>
    </div>
    <span class="mt-1 text-gray text-sm">{{ requestOutcomeDescription }}</span>

    <div class="flex mt-auto">
      <base-button variant="transparent-error" class="mx-auto"
                 @click="() => $emit('remove-quick-reply')"
                 :id="`delete-qr-${index + 1}`">
        Delete Quick Reply
      </base-button>
    </div>
  </div>
</template>

<script>
import FullyAutomatedIcon from '@/assets/images/icons/FullyAutomatedIcon.vue'
import PartiallyAutomatedIcon from '@/assets/images/icons/PartiallyAutomatedIcon.vue'
import WrongAnswerIcon from '@/assets/images/icons/WrongAnswerIcon.vue'
import { SECTION_FOCUSES } from '@/store/constants'
import channelModule from '@/store/modules/channel.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import previewModule from '@/store/modules/preview.module'
import assoc from 'ramda/src/assoc'
import tailwind from '../../../../tailwind.config'
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import { OUTCOME_TYPES } from '@/utils/bot-json.constants'
import variableRefsUtils from '@/utils/variable-refs.utils'
import ValidatedInput from '@/components/base-components/ValidatedInput.vue'

const OUTCOME_TYPE_LABEL_MAP = {
  [OUTCOME_TYPES.CUSTOMER_ANSWERED_YES]: 'Fully Automated',
  [OUTCOME_TYPES.CUSTOMER_ANSWERED_PARTIALLY]: 'Partially Automated',
  [OUTCOME_TYPES.CUSTOMER_ANSWERED_NO]: 'Wrong Answer'
}

export default {
  name: 'QuickReplyEditor',

  components: {
    ValidatedInput,
    WithErrorMessage,
    FullyAutomatedIcon,
    PartiallyAutomatedIcon,
    WrongAnswerIcon
  },

  data() {
    return {
      SECTION_FOCUSES
    }
  },

  props: {
    quickReply: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },

  computed: {
    bubbleSelected: chatBubbleModule.bubbleSelected,
    focus: chatBubbleModule.sectionFocus,
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    stateId() {
      return this.bubbleSelected.stateId
    },

    state() {
      return conversationModule.getState(this.stateId)
    },

    requestOutcomeOptions() {
      return [
        {
          text: OUTCOME_TYPE_LABEL_MAP[OUTCOME_TYPES.CUSTOMER_ANSWERED_YES],
          value: OUTCOME_TYPES.CUSTOMER_ANSWERED_YES,
          icon: 'FullyAutomatedIcon'
        },
        {
          text: OUTCOME_TYPE_LABEL_MAP[OUTCOME_TYPES.CUSTOMER_ANSWERED_PARTIALLY],
          value: OUTCOME_TYPES.CUSTOMER_ANSWERED_PARTIALLY,
          icon: 'PartiallyAutomatedIcon'
        },
        {
          text: OUTCOME_TYPE_LABEL_MAP[OUTCOME_TYPES.CUSTOMER_ANSWERED_NO],
          value: OUTCOME_TYPES.CUSTOMER_ANSWERED_NO,
          icon: 'WrongAnswerIcon'
        },
        {
          text: 'None',
          value: null
        }
      ]
    },

    requestOutcome() {
      return this.state.transitions[this.index].requestOutcome || null
    },

    requestOutcomeDescription() {
      const requestOutcomeType = this.requestOutcome ? `"${OUTCOME_TYPE_LABEL_MAP[this.requestOutcome]}"` : 'No'
      return `Analytics: ${requestOutcomeType} automation result is registered when the user clicks on this quick reply`
    },

    isTitleError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Quick reply title is not allowed to be empty!'
    },

    isNonExistingVariableError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.error?.section.includes('qr') && this.errorDescription?.startsWith('Variable')
    },

    showError() {
      return (this.isTitleError || this.isNonExistingVariableError) && this.errorDescription
    },

    focusColor() {
      return tailwind.theme.colors[this.titleCount > this.titleCountLimit ? 'focus-error' : 'focus']
    },

    titleCount() {
      return this.quickReply.title.length
    },

    titleCountLimit() {
      return channelModule.existsNonAmioChat() ? 20 : 25
    },

    validatedValue() {
      return variableRefsUtils.substituteVariableRefs(this.quickReply.title)
    }
  },

  methods: {
    _updateQuickReply(quickReply) {
      this.$emit('update-quick-reply', quickReply)
    },

    updateQuickReplyTitle(title) {
      const updatedQuickReply = assoc('title', title, this.quickReply)
      this._updateQuickReply(updatedQuickReply)

      if (chatBubbleModule.isErrorBubbleFocused() && title) this.error = null
    },

    updateRequestOutcome(requestOutcome) {
      this.$emit('update-request-outcome', requestOutcome)
    },

    isTitleAboveLimit(title) {
      const resolvedTitle = variableRefsUtils.substituteVariableRefs(title)
      return resolvedTitle.length > this.titleCountLimit
    },

    validateTitle(e) {
      if (!this.isTitleAboveLimit(e.target.value)) return

      const substitutedTitle = variableRefsUtils.substituteVariableRefs(e.target.value)
      const test = substitutedTitle.length - this.titleCountLimit
      e.target.value = e.target.value.slice(0, -test)
      this.updateQuickReplyTitle(e.target.value)
    }
  }
}
</script>
